import React, { useState, useEffect } from 'react';
import {
  TargetsHighlightProps,
  GetTargetsHighlightResponse,
} from '@dreamai/types';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../../api';
import ErrorState from '../../../common/ErrorState';
import './targets-highlight-component.scss';

interface TargetsHighlightComponentProps extends TargetsHighlightProps {
  threadId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
}

interface EnrichedTarget {
  knownOrgId: number;
  knownOrgName: string;
  knownOrgLogoUrl: string | null;
  knownOrgDomain: string | null;
  knownOrgDescription: string | null;
  prospectId?: number;
  prospectName?: string | null;
  prospectEmail?: string | null;
  prospectJobTitle?: string | null;
  prospectLinkedinUrl?: string | null;
  prospectReasoning?: string | null;
  prospectIsGeneric?: boolean | null;
  tag?: string;
}

const TargetsHighlightComponent: React.FC<TargetsHighlightComponentProps> = ({
  targets,
  threadId,
  updateThreadViewVisualDescription,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [enrichedTargets, setEnrichedTargets] = useState<EnrichedTarget[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTargetDetails = async () => {
      try {
        const token = await getAccessTokenSilently();
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.post<GetTargetsHighlightResponse>(
          '/campaigns/targets-highlight',
          { targets },
        );

        if (response.data.success) {
          setEnrichedTargets(response.data.targets);
        }
      } catch (err) {
        console.error('Error fetching target details:', err);
        setError('Failed to load target details');
      } finally {
        setLoading(false);
      }
    };

    fetchTargetDetails();
  }, [targets, getAccessTokenSilently]);

  if (loading) {
    return <div className='targets-highlight-component loading'></div>;
  }

  if (error) {
    return <ErrorState title='Failed to load targets' subtitle={error} />;
  }

  return (
    <div className='targets-highlight-component'>
      <div className='targets-header'>
        <span>Campaign plan</span>
        <h2>Target companies</h2>
      </div>

      <div className='targets-list'>
        {enrichedTargets.map((target, index) => (
          <div key={index} className='target-card'>
            <div className='target-content'>
              <div className='organization-section'>
                {target.tag && <div className='target-tag'>{target.tag}</div>}

                <h3 className='org-name'>{target.knownOrgName}</h3>
                {target.knownOrgDescription && (
                  <p className='org-description'>
                    {target.knownOrgDescription}
                  </p>
                )}
                {target.knownOrgDomain && (
                  <a
                    href={`https://${target.knownOrgDomain}`}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='org-domain'
                  >
                    {target.knownOrgDomain}
                  </a>
                )}
              </div>

              {target.prospectId && (
                <div className='prospect-section'>
                  <h4>Key contact:</h4>
                  <div className='prospect-details'>
                    {!target.prospectIsGeneric && target.prospectName && (
                      <p className='prospect-name'>
                        <a
                          href={target.prospectLinkedinUrl || '#'}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='prospect-linkedin'
                        >
                          {target.prospectName}
                        </a>
                      </p>
                    )}

                    {target.prospectEmail && (
                      <p className='prospect-email'>{target.prospectEmail}</p>
                    )}

                    {target.prospectReasoning && (
                      <p className='prospect-reasoning'>
                        {target.prospectReasoning}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TargetsHighlightComponent;
